import axios from "axios";
import { Loader2 } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";
import UploadImage from "../../../components/UploadImage";
import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { useLanguage } from "../../../context/LanguageContext";
import { BaseUrlApi, ErrorMessage } from "../../../lib/api";
import { SubCategoriesData } from "../../../lib/languages";
import EditorTextArea from "../../../components/EditorTextArea";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "../../../components/ui/drawer";
import FileUploadImage from "../../../components/FileUploadImage";

const DrawerAddSubCategory = ({ getAllSubCategories, item }) => {
  const { language } = useLanguage();
  const t = SubCategoriesData[language] || SubCategoriesData["en"];

  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [form, setForm] = useState({
    title_en: "",
    title_ar: "",
    description_en: "",
    description_ar: "",
    status: false,
  });

  const handleAddSubCategory = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();

      if (form.title_ar || form.title_en) {
        formData.append(
          "title",
          JSON.stringify({ ar: form.title_ar, en: form.title_en })
        );
      }

      if (form.description_ar || form.description_en) {
        formData.append(
          "description",
          JSON.stringify({ ar: form.description_ar, en: form.description_en })
        );
      }

      if (image) formData.append("image", image);

      formData.append("status", form.status ? 1 : 0);

      await axios.post(
        `${BaseUrlApi}/categories/${item.id}/sub-category`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      toast(t.subCategoryCreated, {
        description: t.subCategoryCreatedDescription,
      });

      getAllSubCategories();
      setOpen(false);
      ClearAll();
    } catch (error) {
      setErrors(ErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const ClearAll = () => {
    setImage(null);
    setErrors({});
    setForm({
      title_en: "",
      title_ar: "",
      description_en: "",
      description_ar: "",
      status: false,
    });
  };

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Button className="h-10 bg-blue-600 text-white hover:bg-blue-600/80 rounded-xl">
          {t.addNewSubCategory}
        </Button>
      </DrawerTrigger>
      <DrawerContent dir={language === "ar" ? "rtl" : "ltr"}>
        <div className="h-[calc(100vh-2rem)] md:h-fit max-w-7xl w-full mx-auto overflow-auto hide-scrollbar">
          <DrawerHeader
            className={"flex items-center justify-between flex-wrap gap-4"}
          >
            <div className="w-full sm:w-fit">
              <DrawerTitle className="font-bold text-2xl">
                {t.addSubCategory}
              </DrawerTitle>
              <DrawerDescription className="font-medium text-sm">
                {t.createSubCategoryDescription}
              </DrawerDescription>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-fit">
              <Button
                className="w-full sm:w-fit h-10 bg-blue-600 text-white hover:bg-blue-600/80 rounded-xl z-10"
                onClick={handleAddSubCategory}
                disabled={loading}
              >
                {loading && (
                  <div className="animate-spin">
                    <Loader2 />
                  </div>
                )}
                {t.addSubCategory}
              </Button>
              <DrawerClose asChild>
                <Button
                  variant="outline"
                  className="w-full sm:w-fit h-10 border-2 border-blue-600 text-blue-600 hover:text-blue-500 rounded-xl"
                >
                  {t.close}
                </Button>
              </DrawerClose>
            </div>
          </DrawerHeader>

          {/* Inputs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4 relative">
            <div className="col-span-1 space-y-4 md:sticky top-2 self-start">
              {/* Upload Img */}
              <Card>
                <CardContent className="p-4 space-y-4">
                  <p className="font-bold text-xl">{t.Thumbnail}</p>
                  <FileUploadImage
                    onUploadComplete={(url) => setImage(url)}
                    name={"sub-categories"}
                  />
                  {errors?.image && (
                    <p className="text-red-500 font-semibold text-sm">
                      {errors.image[0]}
                    </p>
                  )}
                  <div className="font-normal text-sm text-muted-foreground text-center">
                    {t.imageExtensionMessage}
                  </div>
                </CardContent>
              </Card>
              {/* Status */}
              <Card>
                <CardContent className="p-4 space-y-4">
                  <div className="flex items-center gap-4 justify-between border-b pb-2">
                    <p className="font-bold text-xl">{t.Status}</p>
                    <div
                      className={`size-4 ${
                        !!form?.status ? "bg-green-500" : "bg-red-500"
                      }  rounded-full`}
                    />
                  </div>
                  <div className="grid gap-2">
                    <p className="text-sm">{t.selectStatus}</p>
                    <Select
                      value={form?.status}
                      onValueChange={(e) => setForm({ ...form, status: e })}
                    >
                      <SelectTrigger
                        className="rounded-lg sm:ml-auto"
                        aria-label="Select status"
                      >
                        <SelectValue placeholder={t.selectStatus} />
                      </SelectTrigger>
                      <SelectContent className="rounded-xl">
                        <SelectItem value={true} className="rounded-lg">
                          {t.active}
                        </SelectItem>
                        <SelectItem value={false} className="rounded-lg">
                          {t.inactive}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            </div>
            <div className="col-span-1 lg:col-span-2">
              {/* General information */}
              <Card className="h-full flex flex-col">
                <CardContent className="p-4 space-y-4 flex-1 flex flex-col">
                  <p className="font-bold text-xl border-b pb-2">
                    {t.generalInformation}
                  </p>
                  <div className="space-y-4 flex-1 flex flex-col">
                    <div className="flex items-center gap-4">
                      <div className="flex flex-col gap-2 w-full">
                        <label htmlFor="title_en" className="text-sm">
                          {t.titleEn}
                        </label>
                        <Input
                          id="title_en"
                          name="title_en"
                          type="text"
                          placeholder={t.enterTitleEn}
                          onChange={(e) =>
                            setForm({ ...form, title_en: e.target.value })
                          }
                        />
                        {errors?.title && (
                          <p className="text-red-500 font-semibold text-sm">
                            {errors.title[0]}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2 w-full">
                        <label htmlFor="title_ar" className="text-sm">
                          {t.titleAr}
                        </label>
                        <Input
                          id="title_ar"
                          name="title_ar"
                          type="text"
                          placeholder={t.enterTitleAr}
                          onChange={(e) =>
                            setForm({ ...form, title_ar: e.target.value })
                          }
                        />
                        {errors?.title && (
                          <p className="text-red-500 font-semibold text-sm">
                            {errors.title[0]}
                          </p>
                        )}
                      </div>
                    </div>
                    {/* Description */}
                    <div className="flex flex-col gap-2 flex-1">
                      <label htmlFor="description" className="text-sm">
                        {t.descriptionAr} & {t.descriptionEn}
                      </label>
                      <EditorTextArea
                        form={form}
                        setForm={setForm}
                        en={"description_en"}
                        ar={"description_ar"}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default DrawerAddSubCategory;
